import { motion } from 'framer-motion';
import { 
  Trophy, 
  Star, 
  Zap, 
  Crown, 
  Gem, 
  Target,
  Flame,
  Medal,
  Award,
  Shield,
  Sparkles,
  TrendingUp
} from 'lucide-react';

// Badge definitions with tiers
export const SAVINGS_BADGES = [
  { 
    id: 'bronze_saver',
    name: 'Bronze Saver',
    description: 'Save your first $50',
    requirement: 50,
    icon: Medal,
    color: 'from-amber-600 to-amber-700',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700'
  },
  { 
    id: 'silver_saver',
    name: 'Silver Saver',
    description: 'Save $100 total',
    requirement: 100,
    icon: Medal,
    color: 'from-slate-400 to-slate-500',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600'
  },
  { 
    id: 'gold_saver',
    name: 'Gold Saver',
    description: 'Save $500 total',
    requirement: 500,
    icon: Trophy,
    color: 'from-yellow-400 to-amber-500',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700'
  },
  { 
    id: 'platinum_saver',
    name: 'Platinum Saver',
    description: 'Save $1,000 total',
    requirement: 1000,
    icon: Crown,
    color: 'from-indigo-400 to-purple-500',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700'
  },
  { 
    id: 'diamond_saver',
    name: 'Diamond Saver',
    description: 'Save $2,500 total',
    requirement: 2500,
    icon: Gem,
    color: 'from-cyan-400 to-blue-500',
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-700'
  },
  { 
    id: 'legend_saver',
    name: 'Legendary Saver',
    description: 'Save $5,000 total',
    requirement: 5000,
    icon: Star,
    color: 'from-rose-400 to-pink-500',
    bgColor: 'bg-rose-100',
    textColor: 'text-rose-700'
  },
  { 
    id: 'titan_saver',
    name: 'Titan Saver',
    description: 'Save $10,000 total',
    requirement: 10000,
    icon: Zap,
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-700'
  }
];

export const ACHIEVEMENT_BADGES = [
  {
    id: 'first_contribution',
    name: 'First Steps',
    description: 'Made your first contribution',
    icon: Sparkles,
    color: 'from-green-400 to-emerald-500',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700'
  },
  {
    id: 'club_joiner',
    name: 'Team Player',
    description: 'Joined your first Susu club',
    icon: Shield,
    color: 'from-blue-400 to-indigo-500',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700'
  },
  {
    id: 'streak_7',
    name: 'Week Warrior',
    description: 'Saved for 7 days straight',
    icon: Flame,
    color: 'from-orange-400 to-red-500',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700'
  },
  {
    id: 'streak_30',
    name: 'Monthly Master',
    description: 'Saved for 30 days straight',
    icon: Flame,
    color: 'from-red-500 to-rose-600',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700'
  },
  {
    id: 'goal_crusher',
    name: 'Goal Crusher',
    description: 'Completed a savings goal',
    icon: Target,
    color: 'from-teal-400 to-cyan-500',
    bgColor: 'bg-teal-100',
    textColor: 'text-teal-700'
  },
  {
    id: 'referral_star',
    name: 'Community Builder',
    description: 'Referred a friend who joined',
    icon: Award,
    color: 'from-amber-400 to-yellow-500',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700'
  },
  {
    id: 'early_adopter',
    name: 'Early Adopter',
    description: 'Joined Susu Money early',
    icon: Star,
    color: 'from-purple-400 to-indigo-500',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700'
  }
];

// Get earned badges based on total savings
export const getEarnedSavingsBadges = (totalSavings) => {
  return SAVINGS_BADGES.filter(badge => totalSavings >= badge.requirement);
};

// Get next badge to earn
export const getNextSavingsBadge = (totalSavings) => {
  return SAVINGS_BADGES.find(badge => totalSavings < badge.requirement);
};

// Calculate progress to next badge
export const getBadgeProgress = (totalSavings) => {
  const nextBadge = getNextSavingsBadge(totalSavings);
  if (!nextBadge) return { progress: 100, nextBadge: null };
  
  const earnedBadges = getEarnedSavingsBadges(totalSavings);
  const lastBadge = earnedBadges[earnedBadges.length - 1];
  const startAmount = lastBadge ? lastBadge.requirement : 0;
  
  const progress = ((totalSavings - startAmount) / (nextBadge.requirement - startAmount)) * 100;
  return { progress: Math.min(progress, 100), nextBadge };
};

// Single Badge Component
export function Badge({ badge, earned = true, size = 'md', showDescription = true }) {
  const Icon = badge.icon;
  
  const sizes = {
    sm: { container: 'w-12 h-12', icon: 'w-5 h-5' },
    md: { container: 'w-16 h-16', icon: 'w-7 h-7' },
    lg: { container: 'w-20 h-20', icon: 'w-9 h-9' }
  };

  return (
    <motion.div 
      className={`flex flex-col items-center ${!earned ? 'opacity-40 grayscale' : ''}`}
      whileHover={earned ? { scale: 1.05 } : {}}
      data-testid={`badge-${badge.id}`}
    >
      <div className={`${sizes[size].container} rounded-full bg-gradient-to-br ${badge.color} flex items-center justify-center shadow-lg ${earned ? 'ring-2 ring-white ring-offset-2' : ''}`}>
        <Icon className={`${sizes[size].icon} text-white`} />
      </div>
      {showDescription && (
        <div className="mt-2 text-center">
          <div className={`text-xs font-semibold ${earned ? 'text-stone-800' : 'text-stone-400'}`}>
            {badge.name}
          </div>
          {earned && (
            <div className="text-[10px] text-stone-500">{badge.description}</div>
          )}
        </div>
      )}
    </motion.div>
  );
}

// Badge Grid Display
export function BadgeGrid({ badges, earnedIds = [], title = "Badges" }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
      <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center">
        <Trophy className="w-5 h-5 mr-2 text-amber-500" />
        {title}
      </h3>
      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-4">
        {badges.map(badge => (
          <Badge 
            key={badge.id} 
            badge={badge} 
            earned={earnedIds.includes(badge.id)}
            size="sm"
          />
        ))}
      </div>
    </div>
  );
}

// Progress Card Component
export function BadgeProgressCard({ totalSavings }) {
  const { progress, nextBadge } = getBadgeProgress(totalSavings);
  const earnedBadges = getEarnedSavingsBadges(totalSavings);
  const currentBadge = earnedBadges[earnedBadges.length - 1];

  return (
    <motion.div 
      className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-5 border border-indigo-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="badge-progress-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-stone-900 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-indigo-600" />
            Savings Progress
          </h3>
          <p className="text-sm text-stone-600">
            {nextBadge ? `$${(nextBadge.requirement - totalSavings).toLocaleString()} to next badge` : 'All badges earned!'}
          </p>
        </div>
        {currentBadge && (
          <Badge badge={currentBadge} size="sm" showDescription={false} />
        )}
      </div>

      {nextBadge && (
        <>
          <div className="relative h-3 bg-white rounded-full overflow-hidden mb-2">
            <motion.div
              className={`absolute inset-y-0 left-0 bg-gradient-to-r ${nextBadge.color} rounded-full`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
          <div className="flex justify-between text-xs text-stone-500">
            <span>${totalSavings.toLocaleString()} saved</span>
            <span className="flex items-center">
              <nextBadge.icon className="w-3 h-3 mr-1" />
              {nextBadge.name} (${nextBadge.requirement.toLocaleString()})
            </span>
          </div>
        </>
      )}

      {/* Earned badges preview */}
      {earnedBadges.length > 0 && (
        <div className="mt-4 pt-4 border-t border-indigo-100">
          <div className="text-xs text-stone-500 mb-2">{earnedBadges.length} badge{earnedBadges.length !== 1 ? 's' : ''} earned</div>
          <div className="flex space-x-2">
            {earnedBadges.slice(-4).map(badge => (
              <Badge key={badge.id} badge={badge} size="sm" showDescription={false} />
            ))}
            {earnedBadges.length > 4 && (
              <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center text-xs font-medium text-stone-600">
                +{earnedBadges.length - 4}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default Badge;
